import { NavLink, useLocation, Link } from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const STEPS = [
  { key: "login",   n: 1, label: "Login / Access",           to: null,             disabled: true },
  { key: "core",    n: 2, label: "Core Products",            to: "/portal/core" },
  { key: "premium", n: 3, label: "Premium & Special",        to: "/portal/premium" },
  { key: "summary", n: 4, label: "Order Summary",            to: "/portal/summary" },
  { key: "payment", n: 5, label: "Payment Method",           to: null,             disabled: true, upcoming: true },
  { key: "confirm", n: 6, label: "Order Confirmation",       to: null,             disabled: true, upcoming: true },
];

export default function PortalLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { totalUnits } = useCart();
  const location = useLocation();

  const activeStep = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith("/portal/core")) return "core";
    if (path.startsWith("/portal/premium")) return "premium";
    if (path.startsWith("/portal/summary")) return "summary";
    return "core";
  }, [location.pathname]);

  return (
    <div className="apwo-portal" data-testid="portal-shell">
      <aside className="apwo-portal-sidebar" aria-label="Order steps">
        <div className="apwo-sidebar-brand">
          <span className="apwo-brand-mark">AP</span>
          <div>
            <p className="apwo-brand-title">AP WAREHOUSE</p>
            <p className="apwo-brand-sub">Online Order</p>
          </div>
        </div>

        <nav className="apwo-sidebar-nav" aria-label="Progress">
          {STEPS.map((s) => {
            const state = activeStep === s.key ? "current" : (
              s.disabled ? (s.upcoming ? "upcoming" : "done") : "todo"
            );
            const content = (
              <span className={`apwo-step apwo-step--${state}`}>
                <span className="apwo-step-num">{s.n}</span>
                <span className="apwo-step-body">
                  <span className="apwo-step-label">{s.label}</span>
                  {state === "current" && <span className="apwo-step-status">In Progress</span>}
                  {s.upcoming && <span className="apwo-step-status">Upcoming</span>}
                </span>
              </span>
            );
            return s.to && !s.disabled ? (
              <NavLink key={s.key} to={s.to} data-testid={`nav-${s.key}`}>{content}</NavLink>
            ) : (
              <span key={s.key} data-testid={`nav-${s.key}`} aria-disabled={s.disabled}>{content}</span>
            );
          })}
        </nav>

        <nav className="apwo-sidebar-nav apwo-sidebar-extra" aria-label="Account">
          <NavLink to="/portal/backorders" data-testid="nav-backorders">
            <span className="apwo-step apwo-step--todo">
              <span className="apwo-step-num" aria-hidden>↺</span>
              <span className="apwo-step-body">
                <span className="apwo-step-label">Backorder Status</span>
                <span className="apwo-step-status">Read only</span>
              </span>
            </span>
          </NavLink>
        </nav>

        <div className="apwo-sidebar-help" data-testid="sidebar-help">
          <p className="apwo-help-title">Need help?</p>
          <p className="apwo-help-line">
            Contact AP Warehouse Team<br />
            <a href="mailto:order@autopartsb2b.com.au">order@autopartsb2b.com.au</a><br />
            <a href="tel:+61426266655">0426 266 655</a>
          </p>
        </div>
      </aside>

      <div className="apwo-portal-main">
        <header className="apwo-portal-header">
          <h1 className="apwo-portal-title" data-testid="portal-title">{title}</h1>
          <div className="apwo-portal-header-right">
            <span className="apwo-cart-chip" data-testid="cart-chip">
              <span className="apwo-cart-count">{totalUnits}</span>
              <span className="apwo-cart-label">{totalUnits === 1 ? "unit" : "units"}</span>
            </span>
            <span className="apwo-user-chip">
              {user?.display_name} <span className="apwo-role-tag">{user?.role}</span>
            </span>
            <Link
              to="/portal/orders"
              className="apwo-btn-secondary apwo-btn-quiet"
              data-testid="portal-my-orders-link"
              title="My orders"
            >
              My orders
            </Link>
            <Link
              to="/portal/settings/security"
              className="apwo-btn-secondary apwo-btn-quiet"
              data-testid="portal-settings-link"
              title="Settings"
            >
              Settings
            </Link>
            <button className="apwo-btn-secondary" onClick={logout} data-testid="portal-logout">Sign out</button>
          </div>
        </header>
        <main className="apwo-portal-body">{children}</main>
        <footer className="apwo-portal-footer">
          Buyer: AP WAREHOUSE PTY LTD, ABN 83 144 869 518 &middot; 17–19 Anzac St, Greenacre NSW 2190 &middot;
          Seller: S1 Asia Pacific Traders Pty Ltd trading as AUTOPARTSB2B, ABN 69 650 366 091 &middot;
          <a href="tel:+61426266655">0426 266 655</a> &middot;
          <a href="mailto:order@autopartsb2b.com.au">order@autopartsb2b.com.au</a>
        </footer>
      </div>
    </div>
  );
}
